import React, { useEffect, useMemo } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { ActivityHistoryPage } from '../pages/activity-history/ActivityHistoryPage'
import { ManageWorkspacePage } from '../pages/manage-workspace-page/ManageWorkspacePage'
import ManageCampaignPage from '../pages/manage-campaign-page/ManageCampaignPage'
import { ManageUsersPage } from '../pages/manage-users-page/ManageUsersPage'
import { ManageEventsPageWrapper } from '../pages/manage-events/ManageEvents'
import { ManageGroupsPage } from '../pages/manage-groups-page/ManageGroupsPage'
import { GroupPage } from '../pages/group-page/GroupPage'
import { CampaignsPage } from '../pages/campaigns-page/CampaignsPage'
import { ManagePathwaysPage } from '../pages/manage-pathways-page/ManagePathwaysPage'
import { ManagePathwayPage } from '../pages/manage-pathway-page/ManagePathwayPage'
import { SearchResultsPage } from '../pages/search-results-page/SearchResults'
import { ManageSurveyPage } from '../pages/surveys/manage/ManageSurvey'
import { StudentSurveyPage } from '../pages/surveys/student/StudentSurvey'
import { SurveysPage } from '../pages/surveys/SurveysPage'
import { UserProfileWrapper } from '../pages/user/UserProfilePage'
import { WorkspacePage } from '../pages/workspace-page/WorkspacePage'

import { EventDetailPage } from '../pages/manage-events/components/event-detail/EventDetail'
import { EventsPage } from '../pages/events-page/EventsPage'
import { StudentPathwaysPage } from '../pages/student-pathways-page/StudentPathwaysPage'
import TopicDetailPage, {
  TopicPageMode
} from '../pages/topic-detail-page/TopicDetailPage'
import { AnalyticsPage } from '../pages/analytics/AnalyticsPage'
import { useRouteTransition } from '../app/hooks/routesTransition'
import DeprecatedTopicPage from '../pages/deprecated-topic-page/DeprecatedTopicPage'

import { TopicEditPageQuery } from '../pages/topic-edit-page/TopicEditPage'
import {
  certificationManage,
  certificationUrl,
  eventUrl,
  linkBuilder,
  pathwayUrl,
  staticLinks,
  surveyUrl,
  topicUrl,
  topicUrlViaCertification,
  topicUrlViaPathway,
  workspaceUrl
} from 'shared/linkBuilder'
import { TopicEditErrorBoundary } from '../pages/topic-edit-page/TopicEditErrorBoundary'
import { BulkUsersImportPage } from '../pages/bulk-users-import-page/BulkUsersImportPage'
import { DashboardsPage } from '../pages/dashboards-page/DashboardsPage'
import { ManageDashboardPage } from '../pages/dashboards-page/manage/ManageDashboardPage'
import { EmailBlocklistPage } from '../pages/email-blocklist-page/EmailBlacklistPage'
import { ManageCertificationsPage } from '../pages/certifications-page/ManageCertificationsPage'
import { CertificationPage } from '../pages/certification-page/CertificationPage'
import { UserCertificationsPage } from '../pages/certifications-page/user/UserCertificationsPage'
import { ManageCertificationPage } from '../pages/certifications-page/manage/ManageCertificationPage'
import { CertificateTemplatePreviewPage } from '../pages/certifications-page/templates/CertificateTemplatePreviewPage'
import { CertificateTemplatesPage } from '../pages/certifications-page/templates/CertificateTemplatesPage'

import PrivateRoute from './private-route/PrivateRoute'
import ReactGA from 'react-ga'
import environment from 'shared/environment'
import { AuthSuccess } from '../pages/auth/components/AuthSuccess'
import { VerifyAccountLoggedIn } from '../pages/auth/VerifyAccount'
import { TopicEnrolmentDetailPage } from '../pages/topic-enrolment-detail-page/TopicEnrolmentDetailPage'
import { customSMTPSenderTokenVerificationQueryParam } from '../pages/account/notifications-tab/CustomSMTP'
import { SelfSignupWithSecretLinkForLoggedInUser } from '../pages/SelfSignupWithSecretLinkForLoggedInUser'
import { useFeatureFlags } from '../app/hooks/useFeatureFlags'
import { EventDetailSeriesPage } from '../pages/manage-events/components/event-detail/EventDetailSeries'
import { FullBlockSpinner } from './common/full-block-spinner/FullBlockSpinner'
import { PathwayDetailPage } from 'front-end/src/pages/pathway-detail-page/PathwayDetailPage'
import { HomePage } from '../pages/home-page/HomePage'
import { SharedResourcesPage } from 'front-end/src/pages/home-page/SharedResourcesPage'
import { CertificateTemplateSelectedPage } from '../pages/certifications-page/templates/CertificateTemplateSelected'
import { AccountPage } from '../pages/account/AccountPage'
import { NotFoundPage } from '../pages/NotFoundPage'
import { isSSR } from 'shared/helpers'

const usePageViews = () => {
  let location = useLocation()

  useEffect(() => {
    if (environment.isProduction()) {
      ReactGA.pageview(window.location.pathname + window.location.search)
    }
  }, [location])
}

const useLoadCustomFont = () => {
  useMemo(() => {
    if (!isSSR) {
      import('../app/util/custom-fonts/loadCustomFonts').then(
        ({ loadCustomFonts }) => {
          loadCustomFonts()
        }
      )
    }
  }, [])
}

const LooopPrivateRoutes = () => {
  const location = useLocation()
  const { RouteTransition } = useRouteTransition(location)
  const { featureFlags, loadingFeatureFlags } = useFeatureFlags()

  const isCertificatesDisabled = !featureFlags?.certificates
  const isCampaignsDisabled = !featureFlags?.campaigns

  usePageViews()
  useLoadCustomFont()

  // Prevent false positive redirects with enabled feature flags
  if (loadingFeatureFlags) return <FullBlockSpinner />

  return (
    <RouteTransition>
      <Switch location={location}>
        <PrivateRoute
          path={staticLinks.activityHistory}
          component={ActivityHistoryPage}
        />
        <PrivateRoute
          path="/workspace/:workspaceId/manage"
          component={ManageWorkspacePage}
        />
        <PrivateRoute
          adminOnly
          featureDisabled={isCampaignsDisabled}
          path={staticLinks.campaigns}
          component={CampaignsPage}
        />
        <PrivateRoute
          adminOnly
          featureDisabled={isCertificatesDisabled}
          path={certificationManage}
          component={ManageCertificationPage}
        />
        <PrivateRoute
          id="TopicDetailPage"
          path={topicUrlViaCertification}
          featureDisabled={isCertificatesDisabled}
          render={(routeProps: any) => (
            <TopicDetailPage
              mode={TopicPageMode.WorkspaceView}
              {...routeProps}
            />
          )}
          exact
        />
        <PrivateRoute
          path={certificationUrl}
          featureDisabled={isCertificatesDisabled}
          component={CertificationPage}
        />
        <PrivateRoute
          adminOnly
          featureDisabled={isCertificatesDisabled}
          path={staticLinks.certificationsManage}
          component={ManageCertificationsPage}
        />
        <PrivateRoute
          path={staticLinks.certifications}
          component={UserCertificationsPage}
          featureDisabled={isCertificatesDisabled}
        />
        <PrivateRoute
          adminOnly
          path="/campaign/:campaignId"
          component={ManageCampaignPage}
          featureDisabled={isCampaignsDisabled}
        />
        <PrivateRoute
          adminOnly
          path={staticLinks.users}
          exact
          component={ManageUsersPage}
        />
        <PrivateRoute
          adminOnly
          path={staticLinks.userImports}
          component={BulkUsersImportPage}
        />
        <PrivateRoute
          id="User"
          path="/user"
          render={() => {
            return <Redirect to={linkBuilder.userDetail('me', 'basic')} />
          }}
          exact //redirect from old URL
        />
        <PrivateRoute path="/user/:userId" component={UserProfileWrapper} />
        <PrivateRoute path="/events" component={EventsPage} />
        <PrivateRoute
          adminOnly
          path="/manageEvents"
          component={ManageEventsPageWrapper}
        />
        <PrivateRoute
          path="/event/series/:eventId"
          component={EventDetailSeriesPage}
        />
        <PrivateRoute path={eventUrl} component={EventDetailPage} />
        <PrivateRoute path="/search/:query" component={SearchResultsPage} />
        <PrivateRoute adminOnly path="/groups" component={ManageGroupsPage} />
        <PrivateRoute path="/group/id/:groupId" component={GroupPage} />
        <PrivateRoute
          path="/workspace/:workspaceId/learn/topic/:enrolmentId"
          component={DeprecatedTopicPage}
        />
        <PrivateRoute path={workspaceUrl} component={WorkspacePage} />
        <PrivateRoute
          id="TopicDetailPage"
          path="/topic/:topicId/preview"
          exact
          render={(routeProps: any) => (
            <TopicDetailPage mode={TopicPageMode.Preview} {...routeProps} />
          )}
        />
        <PrivateRoute
          id="TopicDetailPage"
          path="/topic/:topicId/videoAssessmentGradeBook"
          render={(routeProps: any) => (
            <TopicDetailPage mode={TopicPageMode.Gradebook} {...routeProps} />
          )}
          exact
        />
        <PrivateRoute
          id="TopicDetailPage"
          path="/topic/:topicId/feedback/:feedbackTopicEnrolmentId"
          render={(routeProps: any) => (
            <TopicDetailPage
              mode={TopicPageMode.WorkspaceView}
              {...routeProps}
            />
          )}
          exact
        />
        <PrivateRoute
          id="TopicEnrolmentDetailPage"
          path="/topic-enrolment/:enrolmentId"
          render={(routeProps: any) => (
            <TopicEnrolmentDetailPage
              mode={TopicPageMode.WorkspaceView}
              {...routeProps}
            />
          )}
        />
        <PrivateRoute
          id="TopicEditErrorBoundary"
          path="/topic/:topicId/edit"
          exact
          render={(routeProps: any) => {
            return (
              <TopicEditErrorBoundary topicId={routeProps.match.params.topicId}>
                <TopicEditPageQuery {...routeProps} />
              </TopicEditErrorBoundary>
            )
          }}
        />
        <PrivateRoute
          id="TopicDetailPage"
          path={topicUrl}
          render={(routeProps: any) => (
            <TopicDetailPage
              mode={
                routeProps.match.params.topicVersion
                  ? TopicPageMode.VersionView
                  : TopicPageMode.WorkspaceView
              }
            />
          )}
        />
        <PrivateRoute
          adminOnly
          path="/managePathways"
          exact
          component={ManagePathwaysPage}
        />
        <PrivateRoute
          adminOnly
          path="/managePathway/:pathwayId"
          component={ManagePathwayPage}
        />
        <PrivateRoute path="/pathways" exact component={StudentPathwaysPage} />
        <PrivateRoute
          path="/analytics/:analyticsSection"
          component={AnalyticsPage}
        />
        <PrivateRoute
          adminOnly
          featureDisabled={isCertificatesDisabled}
          exact
          component={CertificateTemplatesPage}
          path={`${staticLinks.certificationsTemplates}`}
        />
        <PrivateRoute
          exact
          path={`${staticLinks.certificationsTemplates}/:templateId`}
          adminOnly
          featureDisabled={isCertificatesDisabled}
          component={CertificateTemplateSelectedPage}
        />
        <PrivateRoute
          exact
          path={`${staticLinks.certificationsTemplates}/:templateId/preview`}
          adminOnly
          featureDisabled={isCertificatesDisabled}
          component={CertificateTemplatePreviewPage}
        />
        <PrivateRoute
          id="PathwayDetailPage"
          path={pathwayUrl}
          component={PathwayDetailPage}
          exact
        />
        <PrivateRoute
          path={staticLinks.dashboards}
          component={DashboardsPage}
        />
        <PrivateRoute
          path="/dashboard/:dashboardId/manage/:tab(setup|audience|collaborators)?"
          component={ManageDashboardPage}
        />
        <PrivateRoute adminOnly path="/surveys" exact component={SurveysPage} />
        <PrivateRoute path={surveyUrl} component={StudentSurveyPage} exact />
        <PrivateRoute
          adminOnly
          path="/surveys/:surveyId/manage/preview"
          component={StudentSurveyPage}
          exact
        />
        <PrivateRoute
          adminOnly
          path="/surveys/:surveyId/manage"
          component={ManageSurveyPage}
        />
        <PrivateRoute
          id="HomePage"
          path={['/', '/dashboard']}
          render={() => (
            <HomePage isCertificatesDisabled={isCertificatesDisabled} />
          )}
          exact
        />
        <PrivateRoute
          path={staticLinks.sharedResources}
          component={SharedResourcesPage}
          exact
        />
        <PrivateRoute
          id="TopicDetailPage"
          path={topicUrlViaPathway}
          render={(routeProps: any) => (
            <TopicDetailPage mode={TopicPageMode.PathwayView} {...routeProps} />
          )}
          exact
        />
        <PrivateRoute adminOnly path="/account" component={AccountPage} />
        <PrivateRoute
          adminOnly
          path={staticLinks.emailBlocklist}
          exact
          component={EmailBlocklistPage}
        />
        {/* // this route is special-it needs to be in both private and public schemas because otherwise a user logging in might see a flash of "Page not found" */}
        <Route path="/authSuccessCallback/:sid" exact component={AuthSuccess} />
        {/* These routes are common with the public router, TODO put them in a single component when we have a different router than react-router */}
        <Route
          path="/verifyAccount/:userId/:token"
          component={VerifyAccountLoggedIn}
        />
        <Route
          path="/emailLink/:userId/activate/:token"
          exact
          render={(props) => {
            return (
              <Redirect
                to={linkBuilder.activateUser(
                  Number(props.match.params.userId),
                  props.match.params.token
                )}
              />
            )
          }}
        />
        <Route
          path="/emailLink/:userId/verifyEmailAddress/:token"
          exact
          render={(props) => {
            return (
              <Redirect
                to={linkBuilder.verifyUser(
                  Number(props.match.params.userId),
                  props.match.params.token
                )}
              />
            )
          }}
        />
        <Route
          path="/emailLink/:userId/verifyAccount/:token"
          exact
          render={(props) => {
            return (
              <Redirect
                to={linkBuilder.verifyUser(
                  Number(props.match.params.userId),
                  props.match.params.token
                )}
              />
            )
          }}
        />
        <Route
          path="/emailLink/verifyCustomSMTPDefaultSender/:token"
          exact
          render={(props) => {
            return (
              <Redirect
                to={`/account/notifications?${customSMTPSenderTokenVerificationQueryParam}=${props.match.params.token}`}
              />
            )
          }}
        />
        <Route
          path="/login"
          render={() => {
            return <Redirect to={staticLinks.home} />
          }}
        />
        <Route
          path="/self-signup/:token"
          component={SelfSignupWithSecretLinkForLoggedInUser}
        />
        <Route component={NotFoundPage} />
      </Switch>
    </RouteTransition>
  )
}

export default LooopPrivateRoutes
